/**
 * TeamCreateDialog — staff-only dialog for creating a company team.
 *
 * Display name, slug (auto-derived from the display name, editable), and
 * comma-separated auto-join email domains.
 * Users signing in with a matching email domain auto-join the team on first
 * sign-in (TRA-823); a domain may feed any number of teams (TRA-1432).
 * 400/409 conflicts (public domain, slug taken) surface as a field-level
 * error; other failures toast.
 */

import { useState, type FormEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import { customFetch, getApiErrorDetail, getApiErrorStatus } from '@/api/client'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/contexts/AuthContext'
import { useTeam } from '@/contexts/TeamContext'
import { SLUG_REGEX, deriveSlug } from '@/lib/slug'

import { parseDomains } from './emailDomains'

interface CreatedTeam {
  id: string
  name: string
  slug: string
}

interface FormState {
  display_name: string
  slug: string
  domains: string
}

const EMPTY_FORM: FormState = { display_name: '', slug: '', domains: '' }

interface TeamCreateDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function TeamCreateDialog({
  open,
  onOpenChange,
}: TeamCreateDialogProps): React.JSX.Element {
  const queryClient = useQueryClient()
  const { signinSilent } = useAuth()
  const { refetch: refetchTeams } = useTeam()
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [slugDirty, setSlugDirty] = useState(false)
  const [fieldError, setFieldError] = useState<string | null>(null)

  const createMutation = useMutation({
    mutationFn: (body: {
      display_name: string
      slug: string
      email_domains: string[]
    }) =>
      customFetch<CreatedTeam>('/api/internal/teams', {
        method: 'POST',
        data: body,
      }),
    onSuccess: async (team: CreatedTeam) => {
      void queryClient.invalidateQueries({ queryKey: ['/api/internal/teams'] })
      toast.success(`Team "${team.name}" created`)
      setForm(EMPTY_FORM)
      setSlugDirty(false)
      setFieldError(null)
      onOpenChange(false)
      // The creator was added as owner+member server-side; refresh the JWT
      // so the team switcher shows the new team and the impersonation picker
      // correctly excludes it (both derive from the groups claim).
      await signinSilent()
      refetchTeams()
    },
    onError: (error: unknown) => {
      const status = getApiErrorStatus(error)
      const detail = getApiErrorDetail(error) ?? 'Failed to create team'
      if (status === 400 || status === 409) {
        setFieldError(detail)
      } else {
        toast.error(detail)
      }
    },
  })

  const isPending = createMutation.isPending
  const slugValid = form.slug === '' || SLUG_REGEX.test(form.slug)
  const canSubmit =
    !isPending &&
    form.display_name.trim().length > 0 &&
    form.slug.length > 0 &&
    slugValid

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault()
    if (!canSubmit) return
    setFieldError(null)
    createMutation.mutate({
      display_name: form.display_name.trim(),
      slug: form.slug,
      email_domains: parseDomains(form.domains),
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create team</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="team-display-name">Name</Label>
            <Input
              id="team-display-name"
              value={form.display_name}
              placeholder="Nolla Health"
              disabled={isPending}
              onChange={e => {
                setForm(prev => ({
                  ...prev,
                  display_name: e.target.value,
                  slug: slugDirty ? prev.slug : deriveSlug(e.target.value),
                }))
              }}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="team-slug">Slug</Label>
            <Input
              id="team-slug"
              value={form.slug}
              placeholder="nolla-health"
              disabled={isPending}
              aria-invalid={!slugValid}
              aria-describedby={slugValid ? undefined : 'team-slug-error'}
              onChange={e => {
                setSlugDirty(true)
                setForm(prev => ({ ...prev, slug: e.target.value }))
              }}
            />
            {!slugValid && (
              <p id="team-slug-error" className="text-sm text-destructive">
                Lowercase letters, digits, hyphens, and dots only.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="team-domains">Auto-join email domains</Label>
            <Input
              id="team-domains"
              value={form.domains}
              placeholder="nollahealth.com, nolla.fi"
              disabled={isPending}
              onChange={e => {
                setForm(prev => ({ ...prev, domains: e.target.value }))
              }}
            />
            <p className="text-sm text-muted-foreground">
              Comma-separated. These domains are allowed through the sign-up
              gate, and new users signing in with them join this team
              automatically. Another team can claim the same domain. Leave
              empty for invite-only.
            </p>
          </div>

          {fieldError && <p className="text-sm text-destructive">{fieldError}</p>}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={isPending}
              onClick={() => {
                onOpenChange(false)
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Create team
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
